import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const SubmissionReviewCard = ({ submission, getStatusBadgeColor, formatStatus }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedReview, setExpandedReview] = useState(null);

  const getScoreColor = (score) => {
    if (score >= 7) return 'text-success';
    if (score >= 5) return 'text-warning';
    return 'text-error ';
  };

  const getDecisionBadge = (decision) => {
    const badges = {
      'accept': { color: 'bg-success-subtle text-success', label: 'Accept' },
      'minor_revision': { color: 'bg-primary-subtle text-primary', label: 'Minor Revision' },
      'major_revision': { color: 'bg-accent-subtle text-accent', label: 'Major Revision' },
      'reject': { color: 'bg-error-subtle text-error', label: 'Reject' }
    };
    return badges?.[decision] || { color: 'bg-muted text-foreground', label: decision };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const decisionReleased = submission?.decisionReleased;
  const hasReviews = decisionReleased && submission?.reviews?.length > 0;
  const avgScore = submission?.aggregateScores?.avgOverall;

  return (
    <>
      {/* Table Row */}
      <tr
        className="cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Title */}
        <td>
          <div className="flex items-center gap-2">
            <Icon
              name={expanded ? 'ChevronDown' : 'ChevronRight'}
              size={16}
              className="text-muted-foreground flex-shrink-0"
            />
            <div>
              <div className="font-medium text-foreground">{submission?.title}</div>
              {submission?.trackName && (
                <div className="text-xs text-muted-foreground mt-0.5">{submission?.trackName}</div>
              )}
            </div>
          </div>
        </td>

        {/* Conference */}
        <td>
          <div className="text-foreground">{submission?.conferenceName}</div>
        </td>

        {/* Status */}
        <td>
          <span className={`px-2 py-1 text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(submission?.status)}`}>
            {formatStatus(submission?.status)}
          </span>
        </td>

        {/* Score */}
        <td className="text-center">
          {hasReviews && avgScore != null ? (
            <span className={`font-bold ${getScoreColor(avgScore)}`}>
              {avgScore?.toFixed(1)}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </td>

        {/* Reviews */}
        <td className="text-center">
          <span className="text-foreground">{submission?.reviewCount || 0}</span>
        </td>

        {/* Date */}
        <td>
          <span className="text-muted-foreground">{formatDate(submission?.submittedAt)}</span>
        </td>
      </tr>
      {/* Expanded Details Row */}
      {expanded && (
        <tr>
          <td colSpan="6" className="bg-muted/50">
            <div className="p-4 space-y-4">
              {/* Abstract */}
              <div>
                <h4 className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">Abstract</h4>
                <p className="text-sm text-muted-foreground">{submission?.abstract}</p>
                {submission?.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {submission?.keywords?.map((keyword, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-background text-xs text-foreground border border-border">
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Aggregate Scores */}
              {hasReviews && (
                <div>
                  <h4 className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">Aggregate Scores</h4>
                  <div className="grid grid-cols-5 gap-3">
                    <div className="bg-card border border-border p-2 text-center">
                      <div className={`text-lg font-bold ${getScoreColor(submission?.aggregateScores?.avgOverall)}`}>
                        {submission?.aggregateScores?.avgOverall?.toFixed(1) || 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Overall</div>
                    </div>
                    <div className="bg-card border border-border p-2 text-center">
                      <div className="text-lg font-bold text-foreground">
                        {submission?.aggregateScores?.avgOriginality?.toFixed(1) || 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Originality</div>
                    </div>
                    <div className="bg-card border border-border p-2 text-center">
                      <div className="text-lg font-bold text-foreground">
                        {submission?.aggregateScores?.avgTechnicalQuality?.toFixed(1) || 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Technical</div>
                    </div>
                    <div className="bg-card border border-border p-2 text-center">
                      <div className="text-lg font-bold text-foreground">
                        {submission?.aggregateScores?.avgClarity?.toFixed(1) || 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Clarity</div>
                    </div>
                    <div className="bg-card border border-border p-2 text-center">
                      <div className="text-lg font-bold text-foreground">
                        {submission?.aggregateScores?.avgRelevance?.toFixed(1) || 'N/A'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Relevance</div>
                    </div>
                  </div>
                  
                  {submission?.aggregateScores?.minOverall != null && submission?.aggregateScores?.maxOverall != null && (
                    <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Range: {submission?.aggregateScores?.minOverall} - {submission?.aggregateScores?.maxOverall}</span>
                      {submission?.aggregateScores?.varianceOverall > 0 && (
                        <>
                          <span>•</span>
                          <span>Std Dev: {submission?.aggregateScores?.varianceOverall?.toFixed(2)}</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Individual Reviews */}
              <div>
                <h4 className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">
                  Individual Reviews ({submission?.reviews?.length || 0})
                </h4>
                
                {!decisionReleased ? (
                  <div className="text-center py-6 bg-card border border-border">
                    <Icon name="Lock" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Review details are hidden until the decision is released</p>
                    <p className="text-xs text-muted-foreground mt-1">They will appear here after the paper receives its decision.</p>
                  </div>
                ) : !hasReviews ? (
                  <div className="text-center py-6 bg-card border border-border">
                    <Icon name="FileText" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No completed reviews are available for this decision yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {submission?.reviews?.map((review) => (
                      <div key={review?.id} className="border border-border bg-card overflow-hidden">
                        <div 
                          className="p-2 bg-muted/60 cursor-pointer hover:bg-muted transition-colors"
                          onClick={(e) => {
                            e?.stopPropagation();
                            setExpandedReview(expandedReview === review?.id ? null : review?.id);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <Icon 
                                name={expandedReview === review?.id ? 'ChevronDown' : 'ChevronRight'} 
                                size={14} 
                                className="text-muted-foreground"
                              />
                              <span className="font-medium text-foreground text-xs">{review?.reviewerName}</span>
                              <span className={`px-2 py-0.5 text-xs font-medium ${getDecisionBadge(review?.decision)?.color}`}>
                                {getDecisionBadge(review?.decision)?.label}
                              </span>
                              <span className={`font-semibold text-xs ${getScoreColor(review?.overallScore)}`}>
                                {review?.overallScore}/10
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Confidence: {review?.confidenceLevel}/5
                              </span>
                            </div>
                          </div>
                        </div>

                        {expandedReview === review?.id && (
                          <div className="p-3 space-y-2 bg-background text-sm">
                            {/* Score Breakdown */}
                            <div className="grid grid-cols-4 gap-2 pb-2 border-b border-border">
                              <div>
                                <span className="text-xs text-muted-foreground">Originality:</span>
                                <span className="ml-1 font-medium text-foreground">{review?.originalityScore}</span>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Technical:</span>
                                <span className="ml-1 font-medium text-foreground">{review?.technicalQualityScore}</span>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Clarity:</span>
                                <span className="ml-1 font-medium text-foreground">{review?.clarityScore}</span>
                              </div>
                              <div>
                                <span className="text-xs text-muted-foreground">Relevance:</span>
                                <span className="ml-1 font-medium text-foreground">{review?.relevanceScore}</span>
                              </div>
                            </div>

                            {/* Detailed Feedback */}
                            {review?.detailedFeedback && (
                              <div>
                                <h5 className="text-xs font-semibold text-foreground mb-1">Detailed Feedback</h5>
                                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{review?.detailedFeedback}</p>
                              </div>
                            )}

                            {/* Strengths */}
                            {review?.strengths && (
                              <div>
                                <h5 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                                  <Icon name="ThumbsUp" size={12} className="text-success" />
                                  Strengths
                                </h5>
                                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{review?.strengths}</p>
                              </div>
                            )}

                            {/* Weaknesses */}
                            {review?.weaknesses && (
                              <div>
                                <h5 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                                  <Icon name="ThumbsDown" size={12} className="text-error " />
                                  Weaknesses
                                </h5>
                                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{review?.weaknesses}</p>
                              </div>
                            )}

                            {/* Questions for Authors */}
                            {review?.questionsForAuthors && (
                              <div>
                                <h5 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1">
                                  <Icon name="HelpCircle" size={12} className="text-primary" />
                                  Questions for Authors
                                </h5>
                                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{review?.questionsForAuthors}</p>
                              </div>
                            )}

                            {/* General Comments */}
                            {review?.generalComments && (
                              <div>
                                <h5 className="text-xs font-semibold text-foreground mb-1">General Comments</h5>
                                <p className="text-xs text-muted-foreground whitespace-pre-wrap">{review?.generalComments}</p>
                              </div>
                            )}

                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default SubmissionReviewCard;
